import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Heart, ShieldCheck, X } from "lucide-react";

/**
 * Covenant modal — shown ONCE on first login for any member whose user.covenant_accepted_at
 * is empty. The modal is non-dismissible without acceptance (no X, no backdrop-close),
 * because that's the whole point of a covenant.
 *
 * After registration we set covenant_accepted_at server-side, so new sign-ups skip this.
 * It only fires for legacy members who registered before the covenant existed.
 */
export default function CovenantGate() {
  const { user, setUser } = useAuth();
  const [text, setText] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [busy, setBusy] = useState(false);

  const needs = user && !user.covenant_accepted_at;

  useEffect(() => {
    if (!needs) return;
    api.get("/covenant").then(({ data }) => setText(data.text || "")).catch(() => {});
  }, [needs]);

  if (!needs) return null;

  const accept = async () => {
    if (!agreed || busy) return;
    setBusy(true);
    try {
      const { data } = await api.post("/auth/me/accept-covenant", { accepted: true });
      setUser(data);
    } catch {
      // Re-show the modal on next mount if it failed.
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-jjblue/70 backdrop-blur-md flex items-center justify-center px-4" data-testid="covenant-gate">
      <div className="jj-card max-w-lg w-full p-7 sm:p-8 relative shadow-2xl">
        <div className="h-12 w-12 rounded-full bg-burgundy/10 text-burgundy flex items-center justify-center">
          <ShieldCheck className="h-6 w-6" strokeWidth={1.7} />
        </div>
        <div className="overline text-burgundy mt-4">Our Covenant</div>
        <h2 className="font-serif text-3xl text-jjblue mt-1 leading-tight">
          Before we go on — read this with me.
        </h2>
        <p className="mt-4 text-jjblue/90 italic leading-relaxed border-l-2 border-gold pl-4">
          &ldquo;{text || "I will share only what is inspiring, uplifting, motivational, or honest information. I will not gossip, slander, manipulate, or post content that doesn't honor Christ or this family."}&rdquo;
        </p>
        <p className="mt-4 text-sm text-[var(--jj-text-secondary)] leading-relaxed">
          This is the air we breathe here. The Lord built this house for the inspired and the
          honest — not for gossip, fear, or noise. Walk with us by agreeing below.
        </p>

        <label className="mt-5 flex items-start gap-3 cursor-pointer select-none" data-testid="covenant-checkbox-row">
          <input
            type="checkbox"
            checked={agreed}
            onChange={(e) => setAgreed(e.target.checked)}
            className="mt-1 h-4 w-4 accent-burgundy"
            data-testid="covenant-checkbox"
          />
          <span className="text-sm text-jjblue">
            I agree to live by this covenant in everything I post, comment, share, and send here.
          </span>
        </label>

        <div className="mt-5">
          <button
            onClick={accept}
            disabled={!agreed || busy}
            className="btn-primary w-full !py-3 inline-flex items-center justify-center gap-2 disabled:opacity-50"
            data-testid="covenant-accept"
          >
            <Heart className="h-4 w-4" strokeWidth={1.8} /> {busy ? "Recording…" : "I agree — let me in"}
          </button>
        </div>
      </div>
    </div>
  );
}
